// S9 Environments and addresses (REQ-1.5): add, rename and remove environments; add and remove
// addresses. Addresses are unique across every app: the server's refusal shows next to the field.
import type { AppView, EnvironmentView } from '@feedback/shared/contracts/apps';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api, seg } from '@/lib/api';
import { errorText, fieldError, fieldErrors } from '../../lib/admin-api';
import { Button, ConfirmModal, Field, Input, Panel } from '../../ui/primitives';
import { appPath, useAppWrite } from '../app-data';

function addressText(a: { host: string; pathPrefix: string }): string {
  return `${a.host}${a.pathPrefix === '/' ? '/' : a.pathPrefix}`;
}

function EnvironmentCard({ app, env }: { app: AppView; env: EnvironmentView }) {
  const readOnly = !app.can.manageSettings;
  const [name, setName] = useState(env.name);
  const [address, setAddress] = useState('');
  const [removing, setRemoving] = useState(false);
  const envPath = appPath(app.id, `/environments/${seg(env.id)}`);
  const rename = useAppWrite(
    app.id,
    (n: string) => api.patch<AppView>(envPath, { name: n }),
    `Renamed to ${name.trim()}`,
  );
  const addAddress = useAppWrite(
    app.id,
    (a: string) => api.post<AppView>(`${envPath}/addresses`, { address: a }),
    'Address added',
  );
  const removeAddress = useAppWrite(
    app.id,
    (id: string) => api.delete<AppView>(`${envPath}/addresses/${seg(id)}`),
    'Address removed',
  );
  const removeEnv = useAppWrite(app.id, () => api.delete<AppView>(envPath), `${env.name} removed`);

  return (
    <li className="rounded-md border border-border p-3">
      <div className="flex flex-wrap items-end gap-2">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            rename.mutate(name.trim());
          }}
        >
          <Field label={`Name (code ${env.code})`} error={rename.isError ? errorText(rename.error) : undefined}>
            {({ id, invalid }) => (
              <Input
                id={id}
                aria-invalid={invalid}
                value={name}
                disabled={readOnly}
                onChange={(e) => setName(e.target.value)}
                className="w-56"
              />
            )}
          </Field>
          {readOnly ? null : (
            <Button size="md" type="submit" busy={rename.isPending} disabled={name.trim() === env.name || !name.trim()}>
              Rename
            </Button>
          )}
        </form>
        {readOnly ? null : (
          <Button variant="ghost" className="ml-auto text-danger" onClick={() => setRemoving(true)}>
            <Trash2 aria-hidden="true" className="size-4" /> Remove environment
          </Button>
        )}
      </div>

      <h3 className="mt-3 text-sm font-medium">Addresses</h3>
      <ul className="mt-1 flex flex-col gap-1">
        {env.addresses.length === 0 ? (
          <li className="text-sm text-muted">No addresses: the widget appears nowhere.</li>
        ) : null}
        {env.addresses.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-2 text-sm">
            <span className="font-mono break-all">{addressText(a)}</span>
            {readOnly ? null : (
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Remove ${addressText(a)}`}
                busy={removeAddress.isPending && removeAddress.variables === a.id}
                onClick={() => removeAddress.mutate(a.id)}
              >
                <Trash2 aria-hidden="true" className="size-4" />
              </Button>
            )}
          </li>
        ))}
      </ul>
      {removeAddress.isError ? <p className="mt-1 text-xs text-danger">{errorText(removeAddress.error)}</p> : null}
      {readOnly ? null : (
        <form
          className="mt-2 flex flex-wrap items-start gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            addAddress.mutate(address.trim(), { onSuccess: () => setAddress('') });
          }}
        >
          <Field
            label="Add an address"
            className="min-w-0 flex-1"
            error={
              addAddress.isError
                ? (fieldError(fieldErrors(addAddress.error), 'address') ?? errorText(addAddress.error))
                : undefined
            }
          >
            {({ id, invalid, describedBy }) => (
              <Input
                id={id}
                aria-invalid={invalid}
                aria-describedby={describedBy}
                value={address}
                placeholder="uat-bookings.hpg.au"
                onChange={(e) => setAddress(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" className="mt-6" busy={addAddress.isPending} disabled={!address.trim()}>
            <Plus aria-hidden="true" className="size-4" /> Add address
          </Button>
        </form>
      )}

      <ConfirmModal
        open={removing}
        onOpenChange={(open) => {
          setRemoving(open);
          if (!open) removeEnv.reset();
        }}
        title={`Remove ${env.name}?`}
        description="Its addresses go too, so the widget stops appearing there. An environment with tickets can’t be removed."
        confirmLabel="Remove environment"
        danger
        busy={removeEnv.isPending}
        error={removeEnv.isError ? errorText(removeEnv.error) : undefined}
        onConfirm={() => removeEnv.mutate(undefined, { onSuccess: () => setRemoving(false) })}
      />
    </li>
  );
}

export function EnvironmentsSection({ app }: { app: AppView }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const create = useAppWrite(
    app.id,
    (body: { code: string; name: string }) => api.post<AppView>(appPath(app.id, '/environments'), body),
    'Environment added',
  );
  const errors = fieldErrors(create.error);

  return (
    <Panel title="Environments and addresses" description="Each environment has its own addresses and AI settings.">
      <p className="mb-3 text-sm">
        Anyone who can open the app’s pages sees the tab; they sign in with their HPG account to send.
      </p>
      <ul className="flex flex-col gap-3">
        {app.environments.map((env) => (
          <EnvironmentCard key={`${env.id}:${env.name}`} app={app} env={env} />
        ))}
      </ul>
      {app.can.manageSettings ? (
        <form
          className="mt-4 flex flex-wrap items-start gap-2 border-t border-border pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(
              { code: code.trim(), name: name.trim() },
              {
                onSuccess: () => {
                  setCode('');
                  setName('');
                },
              },
            );
          }}
        >
          <Field label="New environment name" error={fieldError(errors, 'name')}>
            {({ id, invalid }) => (
              <Input
                id={id}
                aria-invalid={invalid}
                value={name}
                placeholder="UAT"
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </Field>
          <Field label="Code" hint="Lower-case, e.g. uat" error={fieldError(errors, 'code')}>
            {({ id, invalid, describedBy }) => (
              <Input
                id={id}
                aria-invalid={invalid}
                aria-describedby={describedBy}
                value={code}
                placeholder="uat"
                onChange={(e) => setCode(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" className="mt-6" busy={create.isPending} disabled={!code.trim() || !name.trim()}>
            <Plus aria-hidden="true" className="size-4" /> Add environment
          </Button>
          {create.isError && Object.keys(errors).length === 0 ? (
            <p role="alert" className="w-full text-xs font-medium text-danger">
              {errorText(create.error)}
            </p>
          ) : null}
        </form>
      ) : null}
    </Panel>
  );
}
